import { useState, useCallback } from 'react';
import { Modal, notification } from 'antd';
import * as sessionStore from '../services/sessionStore';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styles from './useErrorHandler.module.css';

interface ErrorInfo {
  error: Error;
  context?: string;
  timestamp: Date;
}

export const useErrorHandler = () => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);

  const showErrorModal = (error: any, title: string = 'Помилка завантаження даних') => {
    const errorMessage = error?.response?.data?.detail || 
                        error?.message || 
                        'Виникла невідома помилка';

    Modal.error({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>{errorMessage}</p>
          {error?.response?.status === 500 && (
            <p className={styles.errorHint}>
              Сервер тимчасово недоступний. Спробуйте пізніше.
            </p>
          )}
          {!error?.response && (
            <p className={styles.errorHint}>
              Перевірте підключення до інтернету.
            </p>
          )}
        </div>
      ),
      okText: 'Закрити',
      okType: 'danger',
    });
  };

  const showErrorNotification = (error: any, description?: string) => {
    notification.error({
      message: 'Помилка',
      description: description || error?.response?.data?.detail || error?.message || 'Виникла помилка',
      placement: 'topRight',
      duration: 5,
    });
  };

  // SECURITY: Enhanced error handler with monitoring and logging
  const handleError = useCallback((error: Error | string, context?: string) => {
    const errorObj = error instanceof Error ? error : new Error(error);
    const errorInfo: ErrorInfo = {
      error: errorObj,
      context,
      timestamp: new Date()
    };

    // Add to error log
    setErrors(prev => [...prev.slice(-9), errorInfo]); // Keep last 10 errors

    // Log to console
    console.error(`Error ${context ? `in ${context}` : ''}:`, errorObj);

    // Show user-friendly message
    const userMessage = getUserFriendlyMessage(errorObj, context);
    notification.error({
      message: 'Помилка',
      description: userMessage,
      placement: 'topRight',
      duration: 5,
    });

    // In production, send to monitoring service
    if (process.env.NODE_ENV === 'production') {
      reportError(errorObj, context);
    }
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return {
    showErrorModal,
    showErrorNotification,
    handleError,
    clearErrors,
    errors,
    errorCount: errors.length
  };
};

// Convert technical errors to user-friendly messages
const getUserFriendlyMessage = (error: Error, context?: string): string => {
  const message = error.message.toLowerCase();

  // Network errors
  if (message.includes('network') || message.includes('fetch')) {
    return 'Проблема з мережею. Перевірте підключення до інтернету.';
  }

  // Authentication errors
  if (message.includes('unauthorized') || message.includes('401')) {
    return 'Сесія закінчилася. Будь ласка, увійдіть знову.';
  }

  // Validation errors
  if (message.includes('validation') || message.includes('invalid')) {
    return 'Некоректні дані. Перевірте введену інформацію.';
  }

  // Server errors
  if (message.includes('500') || message.includes('server')) {
    return 'Помилка сервера. Спробуйте пізніше.';
  }

  // Context-specific messages
  switch (context) {
    case 'product-upload':
      return 'Не вдалося завантажити товар. Перевірте дані і спробуйте ще раз.';
    case 'order-processing':
      return 'Помилка обробки замовлення. Зверніться до технічної підтримки.';
    case 'file-upload':
      return 'Не вдалося завантажити файл. Перевірте формат і розмір файлу.';
    case 'websocket':
      return 'Втрачено зв\'язок з сервером. Оновлюю з\'єднання...';
    default:
      return 'Сталася непередбачена помилка. Спробуйте ще раз.';
  }
};

// Send error to monitoring service (placeholder)
const reportError = (error: Error, context?: string) => {
  // TODO: Implement actual error reporting service integration
  const errorReport = {
    message: error.message,
    stack: error.stack,
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
    userId: sessionStore.getActiveSession() ? 'admin' : 'anonymous'
  };

  console.log('Error report to be sent:', errorReport);
  
  // Example: Send to external service
  // fetch('/api/errors', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify(errorReport)
  // }).catch(console.error);
};